import {
  ALLOWED_REDIRECT_URIS,
  DEFAULT_SCOPE,
  KNOWN_CLIENTS,
  OAUTH_ERRORS,
  PKCE_MAX_LENGTH,
  PKCE_MIN_LENGTH,
  PKCE_RFC_MIN_LENGTH,
  SCOPE_LABELS,
} from "../constants/oauth";

// Claude's sample authorize URL omits response_type. Flip this to false once a
// real client request is confirmed to include it.
const TOLERATE_MISSING_RESPONSE_TYPE = true;

const CLIENT_ID_PATTERN = /^[A-Za-z0-9._~-]+$/;
const CHALLENGE_PATTERN = new RegExp(
  "^[A-Za-z0-9\\-._~]{" + PKCE_MIN_LENGTH + "," + PKCE_MAX_LENGTH + "}$"
);
// Written as a code-point scan rather than a regex: a control-character
// class means either literal control bytes in this file or escapes that are
// easy to mangle, and both have already bitten this file once.
const hasControlChars = (value) => {
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code < 32 || code === 127) return true;
  }
  return false;
};

// RFC 6749 3.3 scope charset (NQCHAR: %x21 / %x23-5B / %x5D-7E) plus the space
// separator -- printable ASCII except `"` and `\\`. Scanned by code point for
// the same reason as hasControlChars: the equivalent character class needs a
// backslash-escaped `]`, and getting that wrong silently rejects every valid
// scope rather than failing loudly.
const isScopeChar = (code) =>
  code === 32 ||
  code === 33 ||
  (code >= 35 && code <= 91) ||
  (code >= 93 && code <= 126);

const isValidScope = (value) => {
  for (let i = 0; i < value.length; i += 1) {
    if (!isScopeChar(value.charCodeAt(i))) return false;
  }
  return true;
};

const KNOWN_PARAM_KEYS = [
  "client_id",
  "redirect_uri",
  "scope",
  "state",
  "code_challenge",
  "code_challenge_method",
  "response_type",
];

/** Host of a URL-ish string, or "" -- safe to render. Never throws. */
export const safeHost = (raw) => {
  try {
    return new URL(raw).host;
  } catch (e) {
    return "";
  }
};

/**
 * Display name for the requesting client. An unrecognised client is NEVER
 * labelled "Claude"; every card interpolates this value.
 */
// Shown when the client is not one we know; the page shows it in the reader's language.
export const UNKNOWN_CLIENT_NAME = "The requesting application";

export const getClientName = (clientId, redirectUrl) => {
  // hasOwnProperty, not a bare index: client_id passes a charset that permits
  // `__proto__`, `constructor` and `toString`, all of which resolve to truthy
  // Object.prototype members and would otherwise bypass the fallback below --
  // `__proto__` yields an undefined name, which downstream defaults back to
  // "Claude", branding an unregistered client as Claude.
  const known =
    clientId && Object.prototype.hasOwnProperty.call(KNOWN_CLIENTS, clientId)
      ? KNOWN_CLIENTS[clientId]
      : null;
  if (known?.name) return known.name;
  const host = redirectUrl?.hostname ?? "";
  if (host === "claude.ai" || host === "claude.com") return "Claude";
  return UNKNOWN_CLIENT_NAME;
};

/**
 * THE open-redirect gate. Nothing else decides where the browser is sent.
 * Returns a URL instance for an allowlisted redirect_uri, or null.
 */
export const parseRedirectUri = (value) => {
  if (!value || typeof value !== "string" || value.length > 2048) return null;
  // RFC 6749 3.1.2: the redirection endpoint MUST NOT include a fragment.
  if (value.includes("#")) return null;
  // RFC 6749 3.1.2.3: simple string comparison. This is the real check --
  // everything below is belt and braces.
  if (!ALLOWED_REDIRECT_URIS.includes(value)) return null;

  let url;
  try {
    url = new URL(value);
  } catch (e) {
    return null;
  }
  if (url.hash) return null;
  // Blocks https://claude.ai@evil.example credential-in-authority tricks.
  if (url.username || url.password) return null;

  const isLoopback =
    url.hostname === "localhost" || url.hostname === "127.0.0.1";
  if (url.protocol !== "https:" && !(url.protocol === "http:" && isLoopback)) {
    return null;
  }
  if (url.protocol === "http:" && process.env.NODE_ENV === "production") {
    return null;
  }
  return url;
};

/**
 * Validates a redirect URL that the BACKEND returned. parseRedirectUri does an
 * exact-string match and a returned URL legitimately carries extra query
 * params, so the query is stripped before the origin+path is re-checked
 * against the same allowlist.
 */
export const parseRedirectUriForResponse = (raw) => {
  let url;
  try {
    url = new URL(raw);
  } catch (e) {
    return null;
  }
  if (url.username || url.password || url.hash) return null;
  const base = `${url.origin}${url.pathname}`;
  const bare = base.replace(/\/$/, "");
  if (!parseRedirectUri(base) && !parseRedirectUri(bare)) return null;
  return url;
};

/**
 * Validates the inbound /authorize query string.
 *
 * Returns { params, redirectUrl, fatal, oauthError }.
 *   fatal      -> render locally, NEVER redirect (RFC 6749 4.1.2.1: nothing may
 *                 be sent to redirect_uri until redirect_uri itself is proven).
 *   oauthError -> bounce back to the now-trusted redirect_uri.
 * `params` always carries NORMALISED values; that object, never the raw query
 * string, is what gets POSTed and what the UI reads.
 */
export const validateAuthorizeParams = (searchParams) => {
  const fail = (kind, extra) => ({
    params: null,
    redirectUrl: null,
    fatal: { kind, ...extra },
    oauthError: null,
  });

  if (!searchParams || Array.from(searchParams.keys()).length === 0) {
    return fail("no_params");
  }

  const one = (key) => (searchParams.get(key) ?? "").trim();
  // URLSearchParams.get() takes the FIRST value while PHP's request->input()
  // takes the LAST, so ?redirect_uri=good&redirect_uri=evil could be validated
  // on one value and consumed as the other. RFC 6749 3.1 forbids repeats.
  const duplicated = KNOWN_PARAM_KEYS.filter(
    (key) => searchParams.getAll(key).length > 1
  );
  if (duplicated.includes("client_id")) return fail("bad_client");
  if (duplicated.includes("redirect_uri")) return fail("bad_redirect");

  const clientId = one("client_id");
  if (!clientId || clientId.length > 256 || !CLIENT_ID_PATTERN.test(clientId)) {
    return fail("bad_client");
  }

  const rawRedirect = one("redirect_uri");
  if (!rawRedirect) return fail("missing_redirect");
  const redirectUrl = parseRedirectUri(rawRedirect);
  if (!redirectUrl) {
    return fail("bad_redirect", { host: safeHost(rawRedirect) });
  }

  // From here redirect_uri is trusted: every remaining failure is reported to
  // the client instead of rendered locally.
  const state = one("state");
  const reject = (error, description, echoState = true) => ({
    params: {
      client_id: clientId,
      redirect_uri: rawRedirect,
      state: echoState && state ? state : undefined,
    },
    redirectUrl,
    fatal: null,
    oauthError: { error, description },
  });

  if (duplicated.length > 0) {
    return reject(
      OAUTH_ERRORS.INVALID_REQUEST,
      "The request repeated a parameter that may only appear once."
    );
  }

  if (state && (state.length > 1024 || hasControlChars(state))) {
    // The one case where state is deliberately NOT echoed -- it is unusable.
    return reject(
      OAUTH_ERRORS.INVALID_REQUEST,
      "The state parameter is malformed.",
      false
    );
  }

  const responseType = one("response_type");
  if (responseType) {
    if (responseType !== "code") {
      return reject(
        OAUTH_ERRORS.UNSUPPORTED_RESPONSE_TYPE,
        "Only the authorization code flow is supported."
      );
    }
  } else if (!TOLERATE_MISSING_RESPONSE_TYPE) {
    return reject(
      OAUTH_ERRORS.INVALID_REQUEST,
      "response_type is required and must be 'code'."
    );
  }

  const codeChallenge = one("code_challenge");
  if (!codeChallenge || !CHALLENGE_PATTERN.test(codeChallenge)) {
    return reject(
      OAUTH_ERRORS.INVALID_REQUEST,
      "code_challenge is missing or malformed."
    );
  }
  if (codeChallenge.length < PKCE_RFC_MIN_LENGTH) {
    // eslint-disable-next-line no-console
    console.warn(
      "[oauth] code_challenge is shorter than the RFC 7636 minimum of " +
        PKCE_RFC_MIN_LENGTH
    );
  }

  // RFC 7636 4.3 makes the default `plain`, so an absent method must NOT be
  // silently upgraded to S256 -- that would tell the backend S256 was used for
  // a client that will present a plain verifier, and the mismatch would only
  // surface at token exchange with no useful error.
  const rawMethod = one("code_challenge_method");
  if (!rawMethod || rawMethod.toUpperCase() !== "S256") {
    return reject(
      OAUTH_ERRORS.INVALID_REQUEST,
      "code_challenge_method must be S256."
    );
  }

  const rawScope = one("scope");
  let scope = DEFAULT_SCOPE;
  if (rawScope) {
    if (rawScope.length > 512 || !isValidScope(rawScope)) {
      return reject(
        OAUTH_ERRORS.INVALID_SCOPE,
        "The requested scope is not valid."
      );
    }
    scope = Array.from(new Set(rawScope.split(/\s+/).filter(Boolean))).join(" ");
    if (!scope) scope = DEFAULT_SCOPE;
  }

  return {
    params: {
      client_id: clientId,
      redirect_uri: rawRedirect,
      scope,
      // Kept byte-verbatim beyond the initial trim; undefined when absent so
      // the key is omitted entirely rather than sent as "".
      state: state || undefined,
      code_challenge: codeChallenge,
      code_challenge_method: "S256",
    },
    redirectUrl,
    fatal: null,
    oauthError: null,
  };
};

/**
 * Adds params to an already-validated redirect URL. URLSearchParams does the
 * percent-encoding -- never hand-concatenate. Empty values are skipped, which
 * is what keeps `state=` and `state=undefined` off the wire (RFC 6749 4.1.2:
 * echo state exactly when the client sent one, omit the key when it did not).
 */
export const buildRedirectUrl = (redirectUrl, extra) => {
  const url = new URL(redirectUrl.toString());
  Object.entries(extra).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.set(key, String(value));
    }
  });
  return url.toString();
};

export const buildErrorRedirectUrl = (redirectUrl, error, description, state) =>
  buildRedirectUrl(redirectUrl, {
    error,
    error_description: description,
    state,
  });

export const buildSuccessRedirectUrl = (redirectUrl, code, state) =>
  buildRedirectUrl(redirectUrl, { code, state });

/**
 * Human labels for a space-delimited scope string.
 *
 * Only scopes this app recognises are rendered. An unknown token is counted,
 * never echoed: scope is attacker-controllable in a crafted /authorize link,
 * and rendering it verbatim would let that link paint arbitrary copy onto a
 * hajexbolt.com consent screen. The normalised scope string still goes to the
 * backend unchanged -- this affects display only.
 */
export const describeScopes = (scope) => {
  const items = [];
  let unknown = 0;
  (scope || DEFAULT_SCOPE)
    .split(/\s+/)
    .filter(Boolean)
    .forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(SCOPE_LABELS, key)) {
        items.push({ key, label: SCOPE_LABELS[key] });
      } else {
        unknown += 1;
      }
    });
  if (unknown > 0) {
    items.push({
      key: "additional",
      count: unknown,
      label:
        unknown === 1
          ? "1 additional permission requested by the application"
          : `${unknown} additional permissions requested by the application`,
    });
  }
  return items;
};

/**
 * Defensive parser for the get-auth-code response, whose exact shape is not
 * yet pinned down. Covers { result: { code } }, { code }, { data: { code } },
 * nested result.data / data.result, the redirect_uri/redirect_url/location/url
 * variants, a plain string, and error envelopes.
 *
 * There is deliberately NO `state` key in the result: the echoed state must
 * always be the one the client sent us, never one the server made up.
 */
export const extractAuthCodeResult = (payload) => {
  if (typeof payload === "string") {
    const value = payload.trim();
    if (!value) return { empty: true };
    return /^https?:\/\//i.test(value)
      ? { ok: true, redirectUrl: value }
      : { ok: true, code: value };
  }
  if (!payload || typeof payload !== "object") return { empty: true };

  const sources = [
    payload,
    payload.result,
    payload.data,
    payload.result?.data,
    payload.data?.result,
    payload.result?.result,
  ].filter((source) => source && typeof source === "object");

  const pick = (...keys) => {
    for (const source of sources) {
      for (const key of keys) {
        const value = source[key];
        if (typeof value === "string" && value.trim()) return value.trim();
      }
    }
    return undefined;
  };

  const ok = !(
    payload.status === false ||
    payload.success === false ||
    (typeof payload.error === "string" && payload.error)
  );

  return {
    ok,
    code: pick("code", "auth_code", "authorization_code", "authCode"),
    redirectUrl: pick(
      "redirect_uri",
      "redirect_url",
      "redirectUri",
      "redirectUrl",
      "location",
      "url"
    ),
    message: pick("message", "error_description", "error"),
  };
};

/**
 * Sanity-check a value the backend handed us before we send it to the client as
 * ?code=. Without this, any HTTP 200 body is treated as the code -- so an HTML
 * error page, a maintenance notice or a JSON blob would be forwarded to the
 * client verbatim, producing a redirect the user cannot distinguish from
 * success and an exchange that fails on the client's side with no explanation.
 *
 * Deliberately permissive about ALPHABET (the real format is not yet confirmed
 * -- base64url, JWT-like and opaque tokens all pass) but strict about SHAPE:
 * one token, no whitespace, no markup, plausible length.
 */
export const isPlausibleAuthCode = (value) => {
  if (typeof value !== "string") return false;
  const v = value.trim();
  if (v.length < 8 || v.length > 2048) return false;
  return /^[A-Za-z0-9\-._~+/=:]+$/.test(v);
};

export const apiErrorMessage = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.error_description ||
  error?.response?.data?.error ||
  error?.message ||
  "Something went wrong";
